import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';

const MAX_LOAD_ATTEMPTS = 3;

@Component({
  selector: 'app-play-video',
  templateUrl: './play-video.component.html',
  styleUrls: ['./play-video.component.css'],
  standalone: false
})
export class PlayVideoComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() isMobileView = false;
  /** When false the element stays mounted to buffer; playback waits until this becomes true. */
  @Input() shouldPlay = true;
  @Input() videoUrl: string | unknown;
  @Input() posterUrl: string | undefined;
  @Input() fileExtension: string | undefined;
  /** Phone browsers often miss the SignalR phase push while this video is playing. */
  @Output() playbackCaughtUp = new EventEmitter<void>();

  @ViewChild('videoPlayer') videoPlayer!: ElementRef<HTMLVideoElement>;

  showStartOverlay = false;
  isBuffering = true;
  hasError = false;
  loadAttempt = 0;

  private viewReady = false;
  private attachedSrc = '';
  private playGen = 0;
  private warmed = false;
  private retryHandle: ReturnType<typeof setTimeout> | null = null;

  constructor(private cdr: ChangeDetectorRef) {}

  get videoSrc(): string {
    const url = this.videoUrl;
    if (url == null || url === '') {
      return '';
    }
    return typeof url === 'string' ? url.trim() : String(url).trim();
  }

  get mimeType(): string {
    const ext = (this.fileExtension || '').replace(/^\./, '').toLowerCase();
    if (ext === 'webm') {
      return 'video/webm';
    }
    if (ext === 'ogg' || ext === 'ogv') {
      return 'video/ogg';
    }
    if (ext === 'mov') {
      return 'video/quicktime';
    }
    return 'video/mp4';
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.bindNativeVideoEvents();
    this.attachSource();
    if (this.shouldPlay) {
      this.beginPlayback();
    } else {
      this.warmBuffer();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.viewReady) {
      return;
    }

    if (changes['videoUrl']) {
      this.attachSource(true);
    }

    if (changes['shouldPlay']) {
      if (this.shouldPlay) {
        this.beginPlayback();
      } else {
        this.pauseAndRewind();
      }
    }
  }

  ngOnDestroy(): void {
    this.playGen++;
    this.clearRetry();
    this.unbindNativeVideoEvents();
    this.releaseVideo();
  }

  @HostListener('document:visibilitychange')
  onVisibility(): void {
    if (typeof document === 'undefined' || document.visibilityState !== 'visible') {
      return;
    }
    if (this.shouldPlay) {
      this.tryPlay(false);
    }
    this.playbackCaughtUp.emit();
  }

  onEnded(): void {
    this.playbackCaughtUp.emit();
  }

  onFullscreenEnd(): void {
    this.playbackCaughtUp.emit();
  }

  startWithSound(): void {
    this.showStartOverlay = false;
    this.tryPlay(true);
  }

  onPlaying(): void {
    this.isBuffering = false;
    this.hasError = false;
    const video = this.el;
    if (video && this.shouldPlay) {
      this.showStartOverlay = video.muted;
    }
    this.cdr.markForCheck();
  }

  onWaiting(): void {
    if (this.shouldPlay) {
      this.isBuffering = true;
      this.cdr.markForCheck();
    }
  }

  onCanPlay(): void {
    this.isBuffering = false;
    this.cdr.markForCheck();
    if (this.shouldPlay) {
      this.tryPlay(false);
    } else if (!this.warmed) {
      this.warmBuffer();
    }
  }

  onError(): void {
    const video = this.el;
    if (!this.videoSrc) {
      return;
    }
    // Changing src / calling load() can emit a stale error while the new source is fine.
    if (video && video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      return;
    }
    this.loadAttempt++;
    if (this.loadAttempt <= MAX_LOAD_ATTEMPTS) {
      this.clearRetry();
      this.retryHandle = setTimeout(() => {
        const current = this.el;
        if (!current) {
          return;
        }
        if (current.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
          this.hasError = false;
          this.cdr.markForCheck();
          return;
        }
        current.load();
      }, 600 * this.loadAttempt);
      return;
    }
    if (video && !video.error) {
      return;
    }
    this.hasError = true;
    this.isBuffering = false;
    this.cdr.markForCheck();
  }

  retry(): void {
    this.hasError = false;
    this.loadAttempt = 0;
    this.isBuffering = true;
    this.attachedSrc = '';
    this.attachSource(true);
    if (this.shouldPlay) {
      this.beginPlayback();
    } else {
      this.warmBuffer();
    }
  }

  private readonly onNativeEnded = () => this.onEnded();
  private readonly onNativeFullscreenEnd = () => this.onFullscreenEnd();

  private bindNativeVideoEvents(): void {
    const video = this.el;
    if (!video) {
      return;
    }
    video.addEventListener('ended', this.onNativeEnded);
    // iOS fires this when the user leaves the native fullscreen player. Angular
    // does not bind webkit* events through the template reliably.
    video.addEventListener('webkitendfullscreen', this.onNativeFullscreenEnd);
  }

  private unbindNativeVideoEvents(): void {
    const video = this.el;
    if (!video) {
      return;
    }
    video.removeEventListener('ended', this.onNativeEnded);
    video.removeEventListener('webkitendfullscreen', this.onNativeFullscreenEnd);
  }

  private get el(): HTMLVideoElement | undefined {
    return this.videoPlayer?.nativeElement;
  }

  private attachSource(forceLoad = false): void {
    const video = this.el;
    const src = this.videoSrc;
    if (!video || !src) {
      return;
    }
    if (this.attachedSrc === src && !forceLoad) {
      return;
    }
    this.attachedSrc = src;
    this.warmed = false;
    this.hasError = false;
    this.loadAttempt = 0;
    this.isBuffering = true;
    if (video.getAttribute('src') !== src) {
      video.src = src;
    }
    if (forceLoad) {
      video.load();
    }
    this.cdr.markForCheck();
  }

  private beginPlayback(): void {
    const video = this.el;
    if (!video || !this.videoSrc) {
      return;
    }
    this.hasError = false;
    this.showStartOverlay = false;
    try {
      if (video.currentTime > 0.25) {
        video.currentTime = 0;
      }
    } catch {
      // Some browsers throw until metadata is ready; play() still works from the current frame.
    }
    this.tryPlay(false);
  }

  private pauseAndRewind(): void {
    const video = this.el;
    if (!video) {
      return;
    }
    this.playGen++;
    video.pause();
    try {
      video.currentTime = 0;
    } catch {
      // Ignore until metadata is available.
    }
    this.showStartOverlay = false;
    this.cdr.markForCheck();
  }

  /**
   * Kick the network on browsers (iOS especially) that will not buffer until play()
   * is called, then rewind so the live start still begins at 0:00.
   */
  private warmBuffer(): void {
    const video = this.el;
    if (!video || !this.videoSrc || this.shouldPlay || this.warmed) {
      return;
    }
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    const gen = this.playGen;
    const playAttempt = video.play();
    if (!playAttempt) {
      this.warmed = true;
      return;
    }
    playAttempt
      .then(() => {
        if (gen !== this.playGen || this.shouldPlay) {
          return;
        }
        video.pause();
        try {
          video.currentTime = 0;
        } catch {
          // Ignore.
        }
        this.warmed = true;
      })
      .catch(() => {
        this.warmed = true;
      });
  }

  /**
   * Muted play is allowed without a gesture. We then try to unmute; mobile typically
   * rejects that and keeps playing muted until the overlay click.
   */
  private tryPlay(fromUser: boolean): void {
    const video = this.el;
    if (!video || !this.videoSrc || !this.shouldPlay) {
      return;
    }
    if (!video.paused) {
      if (fromUser && video.muted) {
        video.muted = false;
        video.defaultMuted = false;
        this.showStartOverlay = false;
        this.cdr.markForCheck();
      } else if (!video.muted) {
        this.showStartOverlay = false;
      }
      return;
    }

    const gen = ++this.playGen;
    video.playsInline = true;
    video.volume = 1;
    video.muted = !fromUser;
    video.defaultMuted = !fromUser;

    const playAttempt = video.play();
    if (!playAttempt) {
      if (fromUser) {
        this.showStartOverlay = false;
      }
      return;
    }

    playAttempt
      .then(() => {
        if (gen !== this.playGen) {
          return;
        }
        if (fromUser) {
          this.showStartOverlay = false;
          this.isBuffering = false;
          this.cdr.markForCheck();
          return;
        }
        this.upgradeToAudible(video, gen);
      })
      .catch(() => {
        if (gen !== this.playGen) {
          return;
        }
        this.showStartOverlay = true;
        this.cdr.markForCheck();
      });
  }

  private upgradeToAudible(video: HTMLVideoElement, gen: number): void {
    video.muted = false;
    video.defaultMuted = false;
    video.volume = 1;
    const playAttempt = video.play();
    if (!playAttempt) {
      return;
    }
    playAttempt
      .then(() => {
        if (gen !== this.playGen) {
          return;
        }
        this.showStartOverlay = false;
        this.isBuffering = false;
        this.cdr.markForCheck();
      })
      .catch(() => {
        if (gen !== this.playGen) {
          return;
        }
        video.muted = true;
        video.defaultMuted = true;
        this.showStartOverlay = true;
        video.play().catch(() => undefined);
        this.cdr.markForCheck();
      });
  }

  private releaseVideo(): void {
    const video = this.el;
    if (!video) {
      return;
    }
    video.pause();
    video.removeAttribute('src');
    video.load();
    this.attachedSrc = '';
  }

  private clearRetry(): void {
    if (this.retryHandle != null) {
      clearTimeout(this.retryHandle);
      this.retryHandle = null;
    }
  }
}
